import React, { useState } from 'react';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import DataTable, {
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeader,
  DataTableRow,
} from '@amzn/meridian/data-table';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import plusTokens from '@amzn/meridian-tokens/base/icon/plus';
import { useTranslation } from 'src/arb-wrapper';
import { PERMISSION_LIST_PAGE_SIZE } from 'src/store/constants';
import { useBusinessUnitAdminControllerFindPartnersQuery } from 'src/store/leela-api';
import { formatUtcDate } from 'src/utils/formatDateTime';
import { PartnerFormModal } from './PartnerFormModal';

interface PartnersTabProps {
  businessUnitId: string;
}

interface PartnerRow {
  id: string;
  name: string;
  contractExpiry?: string;
}

export const PartnersTab: React.FC<PartnersTabProps> = ({ businessUnitId }) => {
  const { t } = useTranslation();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<PartnerRow | undefined>();
  const { data, isError, isLoading } = useBusinessUnitAdminControllerFindPartnersQuery({
    id: businessUnitId,
    page: 1,
    pageSize: PERMISSION_LIST_PAGE_SIZE,
  });

  if (isLoading) return <Loader />;
  if (isError) return <Alert type="error">{t('access_management_load_error')}</Alert>;

  const partners = (data?.data ?? []) as PartnerRow[];

  return (
    <Column data-testid="access-partners-tab" spacing="300">
      <Row alignmentHorizontal="end">
        <Button data-testid="access-partners-create-button" type="primary" onClick={() => { setIsCreateOpen(true); }}>
          <Icon tokens={plusTokens} />
          {t('access_management_create_partner')}
        </Button>
      </Row>

      <DataTable data-testid="access-partners-table" showBorder showRowDividers width="100%">
        <DataTableHead backgroundColor="secondary">
          <DataTableRow>
            <DataTableHeader>{t('access_management_table_name')}</DataTableHeader>
            <DataTableHeader>{t('access_management_table_contract_expiry')}</DataTableHeader>
            <DataTableHeader width={100}>{t('access_management_table_actions')}</DataTableHeader>
          </DataTableRow>
        </DataTableHead>
        <DataTableBody>
          {partners.length === 0 && (
            <DataTableRow>
              <DataTableCell colSpan={3}>
                <Text color="secondary" data-testid="access-partners-empty" type="b300">
                  {t('access_management_no_partners')}
                </Text>
              </DataTableCell>
            </DataTableRow>
          )}
          {partners.map(partner => (
            <DataTableRow key={partner.id}>
              <DataTableCell>
                <Text type="b300">{partner.name}</Text>
              </DataTableCell>
              <DataTableCell>
                <Text type="b300">
                  {formatUtcDate(
                    partner.contractExpiry,
                    t('access_management_not_available'),
                  )}
                </Text>
              </DataTableCell>
              <DataTableCell>
                <Button
                  aria-label={t('access_management_edit_partner')}
                  data-testid={`access-partner-edit-${partner.id}`}
                  type="icon"
                  onClick={() => { setEditingPartner(partner); }}
                >
                  <Icon tokens={editTokens} />
                </Button>
              </DataTableCell>
            </DataTableRow>
          ))}
        </DataTableBody>
      </DataTable>

      <PartnerFormModal
        businessUnitId={businessUnitId}
        open={isCreateOpen || !!editingPartner}
        partner={editingPartner}
        onClose={() => { setIsCreateOpen(false); setEditingPartner(undefined); }}
      />
    </Column>
  );
};
